import { useChannelStore } from "@/stores/channel.store";
import { injectLocal, provideLocal } from "@vueuse/core";
import { reactive, ref, watch, type InjectionKey } from "vue";

export interface ILoyaltyNotification {
  points: number;
  message: string;
  prevRanking: number | null;
  ranking: number | null;
  id: string;
  hasMultiplierApplied: boolean;
}

const loyaltyNotificationsLogPrefix = "[LoyaltyNotifications] ";

export const loyaltyNotificationsInjectionKey = Symbol(
  "LoyaltyNotifications",
) as InjectionKey<ReturnType<typeof useSetupLoyaltyNotifications>>;

export function useSetupLoyaltyNotifications() {
  const channelStore = useChannelStore();
  const notifications = reactive([] as ILoyaltyNotification[]);
  const idCounter = ref(0);

  async function addNotification(notification: {
    points: number;
    message: string;
    hasMultiplierApplied: boolean;
  }) {
    console.log(
      loyaltyNotificationsLogPrefix + "Notification received",
      notification,
    );

    const prevRanking = channelStore.monthLeaderboardUserRanking;
    // Update the user's ranking in the month leaderboard
    await channelStore.refetchMonthLeaderboardUserRanking();
    const ranking = channelStore.monthLeaderboardUserRanking;

    notifications.push({
      ...notification,
      id: (++idCounter.value).toString(),
      prevRanking,
      ranking,
    });
  }

  function onNotificationDismissed(id: string) {
    const idx = notifications.findIndex((n) => n.id === id);
    console.log(loyaltyNotificationsLogPrefix + "Notification dismissed", id);

    if (idx !== -1) {
      notifications.splice(idx, 1);
    }
  }

  watch(
    idCounter,
    (_, __, onCleanup) => {
      const interval = setInterval(() => {
        const notification = notifications[0];

        if (notification) onNotificationDismissed(notification.id);
      }, 2500);

      onCleanup(() => clearInterval(interval));
    },
    { immediate: true },
  );

  const definition = reactive({
    addNotification,
    notifications,
    onNotificationDismissed,
  });
  provideLocal(loyaltyNotificationsInjectionKey, definition);
  return definition;
}

export function useLoyaltyNotifications() {
  const loyaltyNotifications = injectLocal(loyaltyNotificationsInjectionKey);
  if (!loyaltyNotifications) {
    throw new Error(
      "useLoyaltyNotifications() was called without a parent (or self) calling useSetupLoyaltyNotifications() first",
    );
  }
  return loyaltyNotifications;
}
